import { useCallback, useState } from 'react';
import Container from '../../shared/Container';
import Button from '../../shared/Button';
import { useAsyncData } from '../../../hooks/useAsyncData';
import { useHeroSlider } from '../../../hooks/useHeroSlider';
import {
  getInitialHeroSlides,
  getPublishedHeroSlides,
} from '../../../services/heroSliderService';
import type { HeroSlide } from '../../../data/heroSlides';
import styles from './Hero.module.css';

export default function Hero() {
  // Los slides salen siempre del código (ver el TODO en heroSliderService.ts:
  // el backend real todavía no los implementa), así que el carrusel se pinta ya
  // en el primer render y no hace falta releer nada: aquí no hay ningún panel
  // en esta misma pestaña que pueda cambiarlos mientras tanto.
  const [iniciales] = useState<HeroSlide[]>(getInitialHeroSlides);
  const cargar = useCallback(() => getPublishedHeroSlides(), []);
  const { datos: slides } = useAsyncData<HeroSlide[]>(cargar, iniciales);

  const { activeIndex, goTo, pause, resume } = useHeroSlider(slides.length);
  const [isUserPaused, setIsUserPaused] = useState(false);
  const activeSlide = slides[activeIndex] ?? slides[0];

  const togglePlayback = () => {
    if (isUserPaused) {
      resume();
    } else {
      pause();
    }
    setIsUserPaused((current) => !current);
  };

  if (!activeSlide) return null;

  return (
    <section
      id="top"
      className={styles.hero}
      onMouseEnter={pause}
      onMouseLeave={() => {
        if (!isUserPaused) resume();
      }}
      onFocusCapture={pause}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget) && !isUserPaused) resume();
      }}
    >
      <img
        className={styles.image}
        src={activeSlide.image}
        alt=""
        style={{
          transform: `translate(${activeSlide.imageOffsetX}px, ${activeSlide.imageOffsetY}px) scale(${activeSlide.imageScale})`,
        }}
      />
      <div className={styles.tint} />
      <div className={styles.gradientHorizontal} />
      <div className={styles.gradientVertical} />

      <Container className={styles.content}>
        <div className={styles.textBlock}>
          <div className={styles.eyebrow}>
            <span className={styles.eyebrowBar} aria-hidden="true" />
            Mutua Colaboradora con la Seguridad Social Nº 72
          </div>
          <h1 className={styles.title}>{activeSlide.title}</h1>
          <p className={styles.subtitle}>{activeSlide.subtitle}</p>
          {activeSlide.title.includes('Portal del Paciente') ? (
            <Button
              href="https://pacientes.solimat.com"
              target="_blank"
              rel="noopener noreferrer"
              size="lg"
              className={styles.portalButton}
            >
              Portal Paciente
            </Button>
          ) : null}
          {activeSlide.id === 'static_nueva-web' ? (
            <Button href="/noticias/29" size="lg" className={styles.portalButton}>
              Ver noticia
            </Button>
          ) : null}
          {activeSlide.id === 'static_portal-paciente' ? (
            <Button href="/noticias/30" size="lg" className={styles.portalButton}>
              Ver noticia
            </Button>
          ) : null}
        </div>

        <div className={styles.dots} role="group" aria-label="Controles del carrusel">
          {slides.length > 1 ? (
            <button
              type="button"
              className={styles.playback}
              aria-label={isUserPaused ? 'Reanudar carrusel' : 'Pausar carrusel'}
              onClick={togglePlayback}
            >
              {isUserPaused ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                </svg>
              )}
            </button>
          ) : null}
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              className={index === activeIndex ? `${styles.dot} ${styles.dotActive}` : styles.dot}
              aria-label={`Mostrar diapositiva ${index + 1} de ${slides.length}: ${slide.title}`}
              aria-current={index === activeIndex ? 'true' : undefined}
              onClick={() => goTo(index)}
            />
          ))}
        </div>
        <p className={styles.srOnly} aria-live={isUserPaused ? 'polite' : 'off'}>
          Diapositiva {activeIndex + 1} de {slides.length}: {activeSlide.title}
        </p>
      </Container>
    </section>
  );
}
